import React, { useState, useEffect, useCallback } from 'react';
import { getLeaderModules, submitSurveyForApproval, getTemplateQuestionCount } from '../../api/moduleApi'; 
import AdHocQuestionManager from '../../components/ui/AdHocQuestionManager.jsx';

const DeploySurvey = ({ user, selectedLeader }) => {
  const leaderId = selectedLeader?.account_id || user?.id || null;
  const [leaderModules, setLeaderModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [adhocQuestions, setAdhocQuestions] = useState([]); 
  const [loadingModules, setLoadingModules] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // store dynamic question counts
  const [coreQuestionCount, setCoreQuestionCount] = useState(0);
  const [extraQuestionCount, setExtraQuestionCount] = useState(0);

  // calculate total questions dynamically
  const totalQuestions = coreQuestionCount + extraQuestionCount + adhocQuestions.length;


  // --- Helper to fetch question counts ---
  const fetchQuestionCounts = useCallback(async (moduleId) => {
    // fetch Master Core Template count
    try {
      const coreCounts = await getTemplateQuestionCount('master');
      setCoreQuestionCount(coreCounts.question_count);
    } catch (e) {
      console.error("Error fetching core template count:", e);
    }

    // fetch Leader Module Template count
    try {
      if (moduleId) {
        // The API now accepts the moduleId directly for the Leader template
        const extraCounts = await getTemplateQuestionCount(String(moduleId)); 
        setExtraQuestionCount(extraCounts.question_count);
      } else {
        setExtraQuestionCount(0);
      }
    } catch (e) {
      console.error("Error fetching extra template count:", e);
      setExtraQuestionCount(0);
    }
  }, []);

  // --- Fetch Leader Modules on load ---
  useEffect(() => {
    const fetchModules = async () => {
      setLoadingModules(true);
      if (!leaderId) {
        setLeaderModules([]);
        setSelectedModuleId(null);
        setLoadingModules(false);
        return;
      }
      try {
        const modules = await getLeaderModules(leaderId);
        setLeaderModules(modules);
        if (modules.length > 0) {
          setSelectedModuleId(modules[0].module_id);
          fetchQuestionCounts(modules[0].module_id);
        } else {
          setSelectedModuleId(null);
        }
      } catch (error) {
        console.error("Error fetching leader modules:", error);
      } finally {
        setLoadingModules(false);
      }
    };
    fetchModules();
  }, [leaderId, fetchQuestionCounts]);

  // --- Update counts when module selection changes ---
  useEffect(() => {
    if (selectedModuleId) {
      fetchQuestionCounts(selectedModuleId);
    }
  }, [selectedModuleId, fetchQuestionCounts]);


  // --- HANDLERS ---

  // Ad-hoc questions are managed by the child component, this just captures the text
  const handleAdHocChange = useCallback((text) => {
    // Split the text into an array of non-empty questions
    const questionsArray = text.split('\n').map(q => q.trim()).filter(q => q.length > 0);
    setAdhocQuestions(questionsArray);
  }, []);

  const handleApprove = async () => {
    if (!selectedModuleId) {
      alert("Please select a module.");
      return;
    }

    setIsSubmitting(true);
    
    // Convert adhocQuestions array back to a single text block for the backend API
    const adhocQuestionsText = adhocQuestions.join('\n');

    const payload = {
      module_id: selectedModuleId,
      leader_id: leaderId, // Ensure this is the actual leader ID
      adhoc_questions_text: adhocQuestionsText,
    };

    try {
      const response = await submitSurveyForApproval(payload);
      alert(response.message || "Survey submitted for admin approval!");
      // Reset state upon successful submission
      setAdhocQuestions([]);
    } catch (error) {
      alert(`Action Failed: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingModules) {
    return <p className="p-6 text-gray-500">Loading module list...</p>;
  }
  
  return (
    <div className="p-6 bg-white rounded-xl shadow-lg">
      <h2 className="text-3xl font-bold mb-4 text-indigo-700">Deploy Module Survey</h2>
      <p className="text-gray-600 mb-6">
        Select a module and add any one-time questions before submitting the final survey structure for Admin approval.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        
        {/* Module Selection Card */}
        <div className="p-4 border border-indigo-200 rounded-lg md:col-span-1 bg-indigo-50">
          <h3 className="text-xl font-semibold text-indigo-700 mb-2">1. Select Module</h3>
          <select
            value={selectedModuleId || ''}
            onChange={(e) => setSelectedModuleId(Number(e.target.value))}
            className="w-full p-2 border border-gray-300 rounded-lg text-base focus:ring-indigo-500 focus:border-indigo-500"
            disabled={isSubmitting}
          >
            {leaderModules.map(module => (
              <option key={module.module_id} value={module.module_id}>
                {module.module_name} (S{module.semester})
              </option>
            ))}
          </select>
          <p className="mt-2 text-sm text-gray-600">Total Questions in Survey: <span className="font-bold">{totalQuestions}</span></p>
        </div>

        {/* The admin now uploads the roster, removing the need for this section */}
        
        <div className="p-4 border border-gray-200 rounded-lg md:col-span-2">
          <h3 className="text-lg font-semibold text-gray-700 mb-3">2. Ad-Hoc Questions (One-Time Only)</h3>
          <p className="text-sm text-gray-600 mb-3">
            The survey currently includes: <span className='font-semibold'>{coreQuestionCount} Core Questions</span> and <span className='font-semibold'>{extraQuestionCount} Saved Module Questions</span>. 
            Add any temporary, one-time questions below:
          </p>
          <AdHocQuestionManager onQuestionsChange={handleAdHocChange} />
        </div>
      </div>

      <button
        onClick={handleApprove}
        className="w-full bg-indigo-600 text-white text-lg font-semibold py-3 rounded-xl hover:bg-indigo-700 transition shadow-lg"
        disabled={isSubmitting || !selectedModuleId || totalQuestions === 0} 
      >
        {isSubmitting ? 'Submitting...' : 'Submit Survey for Admin Approval'}
      </button>
    </div>
  );
};

export default DeploySurvey;